import React, { useState, useEffect } from 'react';
import './EventosList.css';
import { apiFetch } from '../../../services/dashboardApi';
import DashboardButton from './ui/DashboardButton';

function EventosList({ token, canEdit = false }) {
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingEvento, setEditingEvento] = useState(null);

    // Estados para búsqueda, filtro y paginado
    const [searchTerm, setSearchTerm] = useState('');
    const [modalidadFilter, setModalidadFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);

    // Estados para formulario de solicitud de información
    const [showSolicitudForm, setShowSolicitudForm] = useState(false);
    const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
    const [solicitudData, setSolicitudData] = useState({
        nombre: '',
        correo: '',
        telefono: '',
        mensaje: ''
    });
    const [solicitudMensaje, setSolicitudMensaje] = useState({ tipo: '', texto: '' });

    useEffect(() => {
        fetchEventos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, modalidadFilter, currentPage]);

    const fetchEventos = async () => {
        try {
            setLoading(true);

            // Construir URL con parámetros de búsqueda, filtro y paginado
            const params = new URLSearchParams({
                page: currentPage,
                page_size: 12
            });

            if (searchTerm.trim()) {
                params.append('search', searchTerm.trim());
            }

            if (modalidadFilter) {
                params.append('modalidad', modalidadFilter);
            }

            const response = await apiFetch(`http://localhost:8000/api/eventos/?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            // Actualizar estados con respuesta paginada
            setEventos(data.results || []);
            setTotalCount(data.count || 0);
            setNextPage(data.next);
            setPrevPage(data.previous);
        } catch (err) {
            console.error('Error al cargar eventos:', err);
            setEventos([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este evento?')) return;
        try {
            const response = await apiFetch(`http://localhost:8000/api/eventos/${id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Evento eliminado exitosamente');
                fetchEventos();
            } else {
                const data = await response.json();
                alert('Error: ' + (data.detail || JSON.stringify(data)));
            }
        } catch (err) {
            alert('Error al eliminar evento: ' + err.message);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reiniciar a página 1 cuando se busca
    };

    const handleModalidadChange = (e) => {
        setModalidadFilter(e.target.value);
        setCurrentPage(1); // Reiniciar a página 1 cuando se filtra
    };

    const handlePreviousPage = () => {
        if (prevPage && currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (nextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const totalPages = Math.ceil(totalCount / 12);

    const abrirFormularioSolicitud = (evento) => {
        setEventoSeleccionado(evento);
        setShowSolicitudForm(true);
        setSolicitudMensaje({ tipo: '', texto: '' });
    };

    const cerrarFormularioSolicitud = () => {
        setShowSolicitudForm(false);
        setEventoSeleccionado(null);
        setSolicitudData({ nombre: '', correo: '', telefono: '', mensaje: '' });
        setSolicitudMensaje({ tipo: '', texto: '' });
    };

    const enviarSolicitudInfo = async (e) => {
        e.preventDefault();

        try {
            const response = await apiFetch('http://localhost:8000/api/solicitudes-eventos/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                    // NO se requiere Authorization - endpoint público
                },
                body: JSON.stringify({
                    evento: eventoSeleccionado.id,
                    ...solicitudData
                })
            });

            const data = await response.json();

            if (response.ok) {
                setSolicitudMensaje({
                    tipo: 'success',
                    texto: data.message || 'Solicitud enviada exitosamente. Nos pondremos en contacto pronto.'
                });
                // Limpiar formulario después de 3 segundos
                setTimeout(() => {
                    cerrarFormularioSolicitud();
                }, 3000);
            } else {
                setSolicitudMensaje({
                    tipo: 'error',
                    texto: data.detail || data.mensaje || 'Error al enviar la solicitud'
                });
            }
        } catch (err) {
            setSolicitudMensaje({
                tipo: 'error',
                texto: 'Error de conexión: ' + err.message
            });
        }
    };

    if (showForm) {
        return (
            <EventoForm
                evento={editingEvento}
                token={token}
                onCancel={() => {
                    setShowForm(false);
                    setEditingEvento(null);
                    fetchEventos();
                }}
            />
        );
    }

    return (
        <div className="eventos-list__container">
            <h3>Gestión de Eventos</h3>
            <p className="muted subtitle">Eventos organizados por ABIS. Puedes ver todos los eventos programados.</p>

            {canEdit && (
                <DashboardButton variant="success" onClick={() => { setEditingEvento(null); setShowForm(true); }}>
                    Crear Nuevo Evento
                </DashboardButton>
            )}

            {/* Buscador y filtros */}
            <div className="filters">
                {/* Buscador */}
                <div className="filters__search">
                    <input
                        type="text"
                        placeholder="Buscar por título o descripción..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-input"
                    />
                </div>

                {/* Filtro de modalidad */}
                <div className="filters__modalidad">
                    <label htmlFor="modalidad" className="muted">Modalidad:</label>
                    <select id="modalidad" value={modalidadFilter} onChange={handleModalidadChange} className="select-input">
                        <option value="">Todas</option>
                        <option value="PRESENCIAL">Presencial</option>
                        <option value="VIRTUAL">Virtual</option>
                        <option value="MIXTA">Mixta</option>
                    </select>
                </div>

                {/* Contador de resultados */}
                <div className="muted results-count">
                    Total: <strong>{totalCount}</strong> evento{totalCount !== 1 ? 's' : ''}
                    {(searchTerm || modalidadFilter) && ` encontrado${totalCount !== 1 ? 's' : ''}`}
                </div>
            </div>

            {loading && <p className="muted">Cargando eventos...</p>}

            {!loading && eventos.length === 0 && (
                <p>
                    {searchTerm || modalidadFilter
                        ? 'No se encontraron eventos con los filtros aplicados.'
                        : 'No hay eventos publicados.'
                    }
                </p>
            )}

            {!loading && Array.isArray(eventos) && eventos.length > 0 && (
                <div className="eventos-list">
                    {eventos.map(evento => (
                        <div key={evento.id} className="evento-card">
                            <h4>{evento.titulo}</h4>

                            <p><strong>Modalidad:</strong> {evento.modalidad}</p>
                            <p><strong>Fecha:</strong> {new Date(evento.fecha).toLocaleString('es-ES')}</p>
                            {evento.lugar && <p><strong>Lugar:</strong> {evento.lugar}</p>}

                            <p className="evento-desc">
                                {evento.descripcion?.substring(0, 200)}{evento.descripcion?.length > 200 ? '...' : ''}
                            </p>

                            {evento.imagen && (
                                <img src={evento.imagen} alt={evento.titulo} className="evento-image" />
                            )}

                            {canEdit && (
                                <div className="evento-actions">
                                    <DashboardButton
                                        variant="warning"
                                        onClick={() => { setEditingEvento(evento); setShowForm(true); }}
                                    >
                                        Editar
                                    </DashboardButton>
                                    <DashboardButton
                                        variant="danger"
                                        onClick={() => handleDelete(evento.id)}
                                    >
                                        Eliminar
                                    </DashboardButton>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Controles de paginación */}
            {!loading && eventos.length > 0 && (
                <div className="pagination-bar">
                    <DashboardButton variant="view" className={`${(!prevPage || currentPage === 1) ? 'btn--disabled' : ''}`} onClick={handlePreviousPage} disabled={!prevPage || currentPage === 1}>← Anterior</DashboardButton>

                    <div className="muted">Página <strong>{currentPage}</strong> de <strong>{totalPages || 1}</strong> ({eventos.length} de {totalCount} eventos)</div>

                    <DashboardButton variant="view" className={`${!nextPage ? 'btn--disabled' : ''}`} onClick={handleNextPage} disabled={!nextPage}>Siguiente →</DashboardButton>
                </div>
            )}

            {/* Modal de Formulario de Solicitud de Información */}
            {showSolicitudForm && eventoSeleccionado && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">Solicitar Información</h3>
                        <p className="muted" style={{ marginBottom: '20px' }}><strong>Evento:</strong> {eventoSeleccionado.titulo}</p>

                        {solicitudMensaje.texto && (
                            <div className={`alert ${solicitudMensaje.tipo === 'success' ? 'alert--success' : 'alert--error'}`}>{solicitudMensaje.texto}</div>
                        )}

                        <form onSubmit={enviarSolicitudInfo}>
                            <div className="form-group">
                                <label className="form-label">Nombre completo *</label>
                                <input type="text" required value={solicitudData.nombre} onChange={(e) => setSolicitudData({ ...solicitudData, nombre: e.target.value })} className="form-input" />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Correo electrónico *</label>
                                <input type="email" required value={solicitudData.correo} onChange={(e) => setSolicitudData({ ...solicitudData, correo: e.target.value })} className="form-input" />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Teléfono (opcional)</label>
                                <input type="tel" value={solicitudData.telefono} onChange={(e) => setSolicitudData({ ...solicitudData, telefono: e.target.value })} className="form-input" />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Mensaje *</label>
                                <textarea required minLength={10} value={solicitudData.mensaje} onChange={(e) => setSolicitudData({ ...solicitudData, mensaje: e.target.value })} placeholder="Escribe tu consulta sobre el evento..." className="form-textarea" />
                                <small className="muted">Mínimo 10 caracteres</small>
                            </div>

                            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
                                <DashboardButton type="button" onClick={cerrarFormularioSolicitud} variant="secondary">Cancelar</DashboardButton>
                                <DashboardButton type="submit" disabled={solicitudMensaje.tipo === 'success'} variant="success" className={solicitudMensaje.tipo === 'success' ? 'btn--disabled' : ''}>{solicitudMensaje.tipo === 'success' ? '✓ Enviado' : 'Enviar Solicitud'}</DashboardButton>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function EventoForm({ evento, token, onCancel }) {
    const [formData, setFormData] = useState({
        titulo: '',
        descripcion: '',
        modalidad: 'PRESENCIAL',
        fecha: '',
        lugar: ''
    });
    const [imagen, setImagen] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (evento) {
            setFormData({
                titulo: evento.titulo || '',
                descripcion: evento.descripcion || '',
                modalidad: evento.modalidad || 'PRESENCIAL',
                fecha: evento.fecha ? evento.fecha.substring(0, 16) : '',
                lugar: evento.lugar || ''
            });
        }
    }, [evento]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setImagen(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const url = evento
                ? `http://localhost:8000/api/eventos/${evento.id}/`
                : 'http://localhost:8000/api/eventos/';
            const method = evento ? 'PATCH' : 'POST';

            // Usar FormData para enviar archivos
            const data = new FormData();
            data.append('titulo', formData.titulo);
            data.append('descripcion', formData.descripcion);
            data.append('modalidad', formData.modalidad);
            data.append('fecha', formData.fecha);
            if (formData.lugar) {
                data.append('lugar', formData.lugar);
            }
            if (imagen) {
                data.append('imagen', imagen);
            }

            const response = await apiFetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`
                    // No incluir Content-Type, el navegador lo configurará automáticamente con boundary
                },
                body: data
            });

            if (response.ok) {
                alert(evento ? 'Evento actualizado exitosamente' : 'Evento creado exitosamente');
                onCancel();
            } else {
                const responseData = await response.json();
                alert('Error: ' + JSON.stringify(responseData));
            }
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="evento-form__container">
            <h3>{evento ? 'Editar Evento' : 'Crear Nuevo Evento'}</h3>
            <form onSubmit={handleSubmit} className="evento-form">
                <div className="form-group">
                    <label>Título:*</label>
                    <input type="text" name="titulo" value={formData.titulo} onChange={handleChange} required className="form-input" />
                </div>

                <div className="form-group">
                    <label>Descripción:*</label>
                    <textarea name="descripcion" value={formData.descripcion} onChange={handleChange} required rows="6" className="form-textarea" />
                    <small className="muted">Mínimo 10 caracteres</small>
                </div>

                <div className="form-group">
                    <label>Modalidad:*</label>
                    <select name="modalidad" value={formData.modalidad} onChange={handleChange} required className="form-input">
                        <option value="PRESENCIAL">Presencial</option>
                        <option value="VIRTUAL">Virtual</option>
                        <option value="MIXTA">Mixta</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Fecha y Hora:*</label>
                    <input type="datetime-local" name="fecha" value={formData.fecha} onChange={handleChange} required className="form-input" />
                </div>

                <div className="form-group">
                    <label>Lugar:</label>
                    <input type="text" name="lugar" value={formData.lugar} onChange={handleChange} placeholder="Opcional (requerido para eventos presenciales)" className="form-input" />
                    <small className="muted">Recomendado para eventos presenciales y mixtos</small>
                </div>

                <div className="form-group">
                    <label>Imagen:</label>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageChange} className="file-input" />
                    {evento && evento.imagen && (
                        <div className="current-image">
                            <p>Imagen actual:</p>
                            <img src={evento.imagen} alt="Preview" className="preview-image" />
                        </div>
                    )}
                    {imagen && (
                        <p className="success-text">Nueva imagen seleccionada: {imagen.name}</p>
                    )}
                    <small className="muted">Formatos: JPG, JPEG, PNG, WEBP</small>
                </div>

                <div className="form-actions">
                    <DashboardButton type="submit" disabled={loading} variant="success" className={loading ? 'btn--disabled' : ''}>{loading ? 'Guardando...' : (evento ? 'Actualizar' : 'Crear')}</DashboardButton>
                    <DashboardButton type="button" onClick={onCancel} variant="secondary">Cancelar</DashboardButton>
                </div>
            </form>
        </div>
    );
}

export default EventosList;
